import React, { useEffect, useState, useMemo, useCallback } from "react";
import { MaterialReactTable } from "material-react-table";
import type { MRT_ColumnDef } from "material-react-table";
import {
  Box,
  CircularProgress,
  Chip,
  Stack,
  Tabs,
  Tab,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import axiosInstance from "../../Utils/axiosInstance";
import Accordion from "react-bootstrap/Accordion";
import { KeyboardArrowDown, KeyboardArrowUp } from "@mui/icons-material";

const PnlSummaryView2 = () => {
  const [futuresData, setFuturesData] = useState<any[]>([]);
  const [swapsData, setSwapsData] = useState<any[]>([]);
  const [costData, setCostData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<"company" | "counterparty">("company");
  const [activeFilter, setActiveFilter] = useState<string>("summary");

  const [isProfitLossExpanded, setIsProfitLossExpanded] = useState(true);
  const [isCostExpanded, setIsCostExpanded] = useState(true);
  const [isFuturesExpanded, setIsFuturesExpanded] = useState(true);
  const [isSwapsExpanded, setIsSwapsExpanded] = useState(true);

  const handleTabChange = (
    _event: React.SyntheticEvent,
    value: "company" | "counterparty"
  ) => {
    setActiveTab(value);
  };

  useEffect(() => {
    const fetchAllData = async () => {
      setLoading(true);
      try {
        const buildQuerySuffix = () => {
          const params = new URLSearchParams();

          if (activeFilter !== "summary") {
            params.append("type", activeFilter);
          }

          if (activeTab === "counterparty") {
            params.append("customer_pnl", "true");
          }

          return params.toString() ? `?${params.toString()}` : "";
        };

        const suffix = buildQuerySuffix();

        const [futuresRes, swapsRes, costRes] = await Promise.all([
          axiosInstance.get(`/paper-app/futures-pnl-summary-v3-single${suffix}`),
          axiosInstance.get(`/paper-app/swaps-pnl-summary-v3-single${suffix}`),
          axiosInstance.get(`/paper-app/pnl-cost-summary-v3-single${suffix}`)
        ]);

        setFuturesData(Array.isArray(futuresRes.data) ? futuresRes.data : []);
        setSwapsData(Array.isArray(swapsRes.data) ? swapsRes.data : []);
        setCostData(Array.isArray(costRes.data) ? costRes.data : []);
      } catch (e) {
        console.error("FETCH ERROR", e);
        setFuturesData([]);
        setSwapsData([]);
        setCostData([]);
      } finally {
        setLoading(false);
      }
    };

    fetchAllData();
  }, [activeFilter, activeTab]);

  useEffect(() => {
    setActiveFilter("summary");
  }, [activeTab]);

  const handleFilterChange = useCallback((type: string) => {
    setActiveFilter(type);
  }, []);

  const formatNumber = useCallback((value: any) => {
    const num = parseFloat(value);
    if (isNaN(num)) return value;
    return num.toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }, []);

  const renderCell = useCallback((value: any) => {
    const num = parseFloat(value);
    if (isNaN(num)) return value;
    return (
      <span style={{ color: num < 0 ? "red" : num > 0 ? "green" : "black" }}>
        {formatNumber(num)}
      </span>
    );
  }, [formatNumber]);

  const sumByKey = useCallback((data: any[], key: string) =>
    data.reduce((sum, row) => {
      const val = parseFloat(row?.[key]);
      return sum + (isNaN(val) ? 0 : val);
    }, 0), []);

  const calculations = useMemo(() => {
    const futuresPnl = sumByKey(futuresData, "PnL");
    const swapsPnl = sumByKey(swapsData, "PnL");
    const costTotal = sumByKey(costData, "Net");
    const dailyChange = sumByKey(futuresData, "Daily Change") + sumByKey(swapsData, "Daily Change");
    const grandTotal = futuresPnl + swapsPnl - costTotal;

    return { futuresPnl, swapsPnl, costTotal, dailyChange, grandTotal };
  }, [futuresData, swapsData, costData, sumByKey]);

  const profitLossData = useMemo(() => [
    {
      label: "Grand Total",
      ...calculations,
    },
  ], [calculations]);

  const getUniqueValues = useCallback((data: any[], key: string) =>
    Array.from(new Set(data.map((row) => row[key]))).filter(Boolean), []);

  const createFilterProps = useMemo(() => ({
    size: "small" as const,
    sx: {
      fontSize: "0.65rem",
      minWidth: "70px",
      "& .MuiInputBase-root": {
        height: "26px",
        fontSize: "0.65rem",
      },
      "& .MuiSelect-select": {
        padding: "0px 4px",
        fontSize: "0.65rem",
        lineHeight: 1.2,
      },
      "& .MuiInputBase-input": {
        padding: "0px 4px",
        fontSize: "0.65rem",
      },
    },
    SelectProps: {
      MenuProps: {
        PaperProps: {
          sx: {
            fontSize: "0.65rem",
            maxHeight: 120,
            "& .MuiMenuItem-root": {
              fontSize: "0.65rem",
              minHeight: "24px",
              padding: "0px 6px",
            },
          },
        },
      },
    },
  }), []);

  const futuresColumns: MRT_ColumnDef<any>[] = useMemo(() => [
    {
      id: "Organization",
      header: "Organization",
      accessorFn: row => row["Organization"],
      filterVariant: "select",
      filterSelectOptions: getUniqueValues(futuresData, "Organization"),
      size: 120,
    },
    {
      id: "Book",
      header: "Book",
      accessorFn: row => row["Book"],
      filterVariant: "select",
      filterSelectOptions: getUniqueValues(futuresData, "Book"),
      size: 100,
    },
    {
      id: "Strategy",
      header: "Strategy",
      accessorFn: row => row["Strategy"],
      filterVariant: "select",
      filterSelectOptions: getUniqueValues(futuresData, "Strategy"),
      size: 100,
    },
    {
      id: "VolumeLots",
      header: "Volume (lots)",
      accessorFn: row => row["Volume(lots)"],
      Cell: ({ cell }) => renderCell(cell.getValue()),
      muiTableBodyCellProps: { align: "right" },
      size: 100,
    },
    {
      id: "Price",
      header: "Price",
      accessorFn: row => row["Price"],
      Cell: ({ cell }) => renderCell(cell.getValue()),
      muiTableBodyCellProps: { align: "right" },
      size: 80,
    },
    {
      id: "MTM",
      header: "MTM",
      accessorFn: row => row["MTM"],
      Cell: ({ cell }) => renderCell(cell.getValue()),
      muiTableBodyCellProps: { align: "right" },
      size: 80,
    },
    {
      id: "YesterdayMTM",
      header: "Yesterday MTM",
      accessorFn: row => row["Yesterday MTM"],
      Cell: ({ cell }) => renderCell(cell.getValue()),
      muiTableBodyCellProps: { align: "right" },
      size: 100,
    },
    {
      id: "DailyChange",
      header: "Daily Change",
      accessorFn: row => row["Daily Change"],
      Cell: ({ cell }) => renderCell(cell.getValue()),
      muiTableBodyCellProps: { align: "right" },
      size: 100,
    },
    {
      id: "PnL",
      header: "PnL",
      accessorFn: row => row["PnL"],
      Cell: ({ cell }) => renderCell(cell.getValue()),
      muiTableBodyCellProps: { align: "right" },
      size: 100,
    },
  ], [futuresData, getUniqueValues, renderCell]);

  const swapsColumns: MRT_ColumnDef<any>[] = useMemo(() => [
    {
      id: "Organization",
      header: "Organization",
      accessorFn: row => row["Organization"],
      filterVariant: "select",
      filterSelectOptions: getUniqueValues(swapsData, "Organization"),
      size: 120,
    },
    {
      id: "Book",
      header: "Book",
      accessorFn: row => row["Book"],
      filterVariant: "select",
      filterSelectOptions: getUniqueValues(swapsData, "Book"),
      size: 100,
    },
    {
      id: "Strategy",
      header: "Strategy",
      accessorFn: row => row["Strategy"],
      filterVariant: "select",
      filterSelectOptions: getUniqueValues(swapsData, "Strategy"),
      size: 100,
    },
    {
      id: "VolumeLots",
      header: "Volume (lots)",
      accessorFn: row => row["Volume(lots)"],
      Cell: ({ cell }) => renderCell(cell.getValue()),
      muiTableBodyCellProps: { align: "right" },
      size: 100,
    },
    {
      id: "Price",
      header: "Price",
      accessorFn: row => row["Price"],
      Cell: ({ cell }) => renderCell(cell.getValue()),
      muiTableBodyCellProps: { align: "right" },
      size: 80,
    },
    {
      id: "MTM",
      header: "MTM",
      accessorFn: row => row["MTM"],
      Cell: ({ cell }) => renderCell(cell.getValue()),
      muiTableBodyCellProps: { align: "right" },
      size: 80,
    },
    {
      id: "YesterdayMTM",
      header: "Yesterday MTM",
      accessorFn: row => row["Yesterday MTM"],
      Cell: ({ cell }) => renderCell(cell.getValue()),
      muiTableBodyCellProps: { align: "right" },
      size: 100,
    },
    {
      id: "DailyChange",
      header: "Daily Change",
      accessorFn: row => row["Daily Change"],
      Cell: ({ cell }) => renderCell(cell.getValue()),
      muiTableBodyCellProps: { align: "right" },
      size: 100,
    },
    {
      id: "PnL",
      header: "PnL",
      accessorFn: row => row["PnL"],
      Cell: ({ cell }) => renderCell(cell.getValue()),
      muiTableBodyCellProps: { align: "right" },
      size: 100,
    },
  ], [swapsData, getUniqueValues, renderCell]);

  const costColumns: MRT_ColumnDef<any>[] = useMemo(() => [
    {
      accessorKey: "Organization",
      header: "Organization",
      filterVariant: "select",
      filterSelectOptions: getUniqueValues(costData, "Organization"),
      enableColumnFilter: true,
      muiFilterTextFieldProps: createFilterProps,
      size: 165,
    },
    {
      accessorKey: "Book",
      header: "Book",
      filterVariant: "select",
      filterSelectOptions: getUniqueValues(costData, "Book"),
      enableColumnFilter: true,
      muiFilterTextFieldProps: createFilterProps,
    },
    {
      accessorKey: "Strategy",
      header: "Strategy",
      filterVariant: "select",
      filterSelectOptions: getUniqueValues(costData, "Strategy"),
      enableColumnFilter: true,
      muiFilterTextFieldProps: createFilterProps,
    },
    {
      accessorKey: "Exchange Fee",
      header: "Exchange Fee",
      Cell: ({ cell }) => renderCell(cell.getValue()),
      muiTableBodyCellProps: { align: "right" },
    },
    {
      accessorKey: "Clearing House Fee",
      header: "Clearing House Fee",
      Cell: ({ cell }) => renderCell(cell.getValue()),
      muiTableBodyCellProps: { align: "right" },
    },
    {
      accessorKey: "Brokerage",
      header: "Brokerage",
      Cell: ({ cell }) => renderCell(cell.getValue()),
      muiTableBodyCellProps: { align: "right" },
    },
    {
      accessorKey: "Net",
      header: "Net",
      Cell: ({ cell }) => renderCell(cell.getValue()),
      muiTableBodyCellProps: { align: "right" },
    },
  ], [costData, getUniqueValues, renderCell, createFilterProps]);

  const profitLossColumns: MRT_ColumnDef<any>[] = useMemo(() => [
    {
      header: "Grand Total",
      accessorKey: "grandTotal",
      Cell: ({ cell }) => renderCell(cell.getValue()),
      muiTableBodyCellProps: { align: "right" },
    },
    {
      header: "Futures PnL",
      accessorKey: "futuresPnl",
      Cell: ({ cell }) => renderCell(cell.getValue()),
      muiTableBodyCellProps: { align: "right" },
    },
    {
      header: "Swaps PnL",
      accessorKey: "swapsPnl",
      Cell: ({ cell }) => renderCell(cell.getValue()),
      muiTableBodyCellProps: { align: "right" },
    },
    {
      header: "Cost",
      accessorKey: "costTotal",
      Cell: ({ cell }) => renderCell(cell.getValue()),
      muiTableBodyCellProps: { align: "right" },
    },
    {
      header: "Daily Change",
      accessorKey: "dailyChange",
      Cell: ({ cell }) => renderCell(cell.getValue()),
      muiTableBodyCellProps: { align: "right" },
    },
  ], [renderCell]);

  const tableProps = useMemo(() => ({
    enableColumnFilters: true,
    enableSorting: true,
    enablePagination: false,
    enablePinning: true,
    initialState: {
      showColumnFilters: true,
      density: "compact" as const,
      pagination: { pageSize: 25, pageIndex: 0 },
      columnPinning: { left: ["Organization", "Book", "Strategy"] },
    },
    columnFilterDisplayMode: "subheader" as const,
    layoutMode: "grid" as const,
    muiTableContainerProps: {
      sx: {
        maxHeight: "70vh",
        border: "1px solid #e0e0e0",
        borderRadius: 2,
        boxShadow: "0 2px 6px rgba(0,0,0,0.08)",
        backgroundColor: "#ffffff",
      },
    },
    muiTableHeadRowProps: {
      sx: {
        position: "sticky",
        top: 0,
        background: "white",
        zIndex: 5,
        "& .MuiTableCell-root": {
          fontWeight: 600,
          fontSize: "0.75rem",
          color: "#2c3e50",
          borderRight: "1px solid #dcdcdc",
        },
      },
    },
    muiTableBodyCellProps: {
      sx: {
        fontSize: "0.7rem",
        padding: "4px 8px",
        borderBottom: "1px solid #f1f1f1",
        "&:hover": {
          backgroundColor: "#fafafa",
        },
      },
    },
    muiTopToolbarProps: {
      sx: {
        backgroundColor: "#fff",
        padding: "8px",
        "& .MuiBox-root.css-wsew38": {
          backgroundColor: "#fff",
          display: "flex",
          alignItems: "center",
          gap: "8px",
        },
      },
      className: "top-toolbar-wrapper__new",
    },
    muiBottomToolbarProps: {
      sx: {
        minHeight: "20px",
        padding: "4px 8px",
        backgroundColor: "#fafafa",
        borderTop: "1px solid #e0e0e0",
      },
    },
  }), []);

  const commonTableStyles = useMemo(() => ({
    muiTableHeadCellProps: {
      sx: {
        fontSize: '0.75rem',
        padding: '8px',
      }
    },
    muiTableBodyCellProps: {
      sx: {
        fontSize: '0.75rem',
        padding: '8px',
      }
    },
    muiTablePaperProps: {
      sx: {
        '& .MuiInputBase-root': {
          fontSize: '0.75rem',
        },
        '& .MuiSelect-select': {
          fontSize: '0.75rem',
          padding: '4px 8px !important',
        },
        '& .MuiTextField-root': {
          '& input': {
            fontSize: '0.75rem',
            padding: '6px 8px',
          }
        }
      }
    },
    muiSelectCheckboxProps: {
      sx: {
        padding: '2px',
      }
    },
    muiFilterTextFieldProps: {
      sx: {
        '& .MuiInputBase-root': {
          fontSize: '0.75rem',
        }
      }
    }
  }), []);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="80vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <div className="page-body">
      <div className="table-ad__wrapper typeCapitalize">
        <Accordion defaultActiveKey="0" className="table-ad__addInputs" style={{ width: "100%" }}>
          <Accordion.Item eventKey="0">
            <Accordion.Header
              className="accordion_button"
              style={{
                fontSize: "0.8rem",
                backgroundColor: "#d1edf1",
                padding: "6px 12px",
                borderRadius: "4px",
                fontWeight: 600,
              }}
            >
              P&L Summary
            </Accordion.Header>

            <Accordion.Body>
              <Tabs
                value={activeTab}
                onChange={handleTabChange}
                indicatorColor="primary"
                textColor="primary"
                variant="scrollable"
                scrollButtons="auto"
                sx={{
                  minHeight: 36,
                  mb: 2,
                  "& .MuiTab-root": {
                    textTransform: "none",
                    fontWeight: 500,
                    fontSize: "0.85rem",
                    px: 2,
                    minHeight: 36,
                    py: 0.5,
                    color: "#555",
                    borderRight: "1px solid #e0e0e0",
                    "&:last-of-type": { borderRight: "none" },
                  },
                  "& .Mui-selected": {
                    color: "#1976d2",
                    backgroundColor: "#ffffff",
                  },
                  "& .MuiTabs-indicator": {
                    height: "2px",
                  },
                }}
              >
                <Tab value="company" label="Company P&L" />
                <Tab value="counterparty" label="Counterparty P&L" />
              </Tabs>

              <Stack direction="row" spacing={1} alignItems="center" mb={2}>
                {["summary", "daily", "monthly"].map((type) => (
                  <Chip
                    key={type}
                    label={type.charAt(0).toUpperCase() + type.slice(1)}
                    size="small"
                    clickable
                    color={activeFilter === type ? "primary" : "default"}
                    onClick={() => handleFilterChange(type)}
                    deleteIcon={<CloseIcon sx={{ fontSize: 14 }} />}
                    onDelete={activeFilter === type && type !== "summary" ? () => setActiveFilter("summary") : undefined}
                    sx={{
                      borderRadius: "4px",
                      fontWeight: activeFilter === type ? 500 : 400,
                      backgroundColor: activeFilter === type ? "#00bcd4" : "#e0e0e0",
                      color: activeFilter === type ? "#fff" : "#000",
                      "& .MuiChip-deleteIcon": {
                        color: "#fff",
                        ml: 0.5,
                      },
                      "&:hover": {
                        backgroundColor: activeFilter === type ? "#00bcd4" : "#e0e0e0",
                      },
                    }}
                  />
                ))}
              </Stack>

              <Box mb={3}>
                <Box
                  sx={{
                    backgroundColor: "#f5f5f5",
                    padding: "6px 10px",
                    borderRadius: "4px",
                    mb: 1,
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    maxWidth: "50%",
                    '&:hover': {
                      backgroundColor: "#ececec"
                    }
                  }}
                  onClick={() => setIsProfitLossExpanded(!isProfitLossExpanded)}
                >
                  <strong style={{ fontSize: "0.8rem", color: "#2c3e50" }}>
                    Profit And Loss
                  </strong>
                  {isProfitLossExpanded ? <KeyboardArrowUp fontSize="small" /> : <KeyboardArrowDown fontSize="small" />}
                </Box>
                {isProfitLossExpanded && (
                  <Box sx={{ maxWidth: "50%" }}>
                    <MaterialReactTable
                      columns={profitLossColumns}
                      data={profitLossData}
                      {...tableProps}
                      enablePagination={false}
                      enableColumnFilters={false}
                    />
                  </Box>
                )}
              </Box>

              <Box mb={3}>
                <Box sx={{
                  backgroundColor: "#f5f5f5",
                  padding: "8px 12px",
                  borderRadius: "4px",
                  mb: 1,
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  '&:hover': {
                    backgroundColor: "#ececec"
                  }
                }}
                  onClick={() => setIsFuturesExpanded(!isFuturesExpanded)}
                >
                  <strong style={{ fontSize: "0.85rem", color: "#2c3e50" }}>Futures</strong>
                  {isFuturesExpanded ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                </Box>
                {isFuturesExpanded && (
                  <MaterialReactTable
                    columns={futuresColumns}
                    data={futuresData}
                    {...tableProps}
                    {...commonTableStyles}
                  />
                )}
              </Box>

              <Box mb={3}>
                <Box
                  sx={{
                    backgroundColor: "#f5f5f5",
                    padding: "8px 12px",
                    borderRadius: "4px",
                    mb: 1,
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    '&:hover': {
                      backgroundColor: "#ececec"
                    }
                  }}
                  onClick={() => setIsSwapsExpanded(!isSwapsExpanded)}
                >
                  <strong style={{ fontSize: "0.85rem", color: "#2c3e50" }}>Swaps</strong>
                  {isSwapsExpanded ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                </Box>
                {isSwapsExpanded && (
                  <MaterialReactTable
                    columns={swapsColumns}
                    data={swapsData}
                    {...tableProps}
                    {...commonTableStyles}
                  />
                )}
              </Box>

              <Box mb={3}>
                <Box
                  sx={{
                    backgroundColor: "#f5f5f5",
                    padding: "8px 12px",
                    borderRadius: "4px",
                    mb: 1,
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    '&:hover': {
                      backgroundColor: "#ececec"
                    }
                  }}
                  onClick={() => setIsCostExpanded(!isCostExpanded)}
                >
                  <strong style={{ fontSize: "0.85rem", color: "#2c3e50" }}>Cost</strong>
                  {isCostExpanded ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                </Box>
                {isCostExpanded && (
                  <MaterialReactTable
                    columns={costColumns}
                    data={costData}
                    {...tableProps}
                    {...commonTableStyles}
                  />
                )}
              </Box>
            </Accordion.Body>
          </Accordion.Item>
        </Accordion>
      </div>
    </div>
  );
};

export default PnlSummaryView2;